/**
 * ThinkMissionNav — MissionNavigator client bridge (page-agent style).
 *
 * Think DO declares get_page_context / navigate / click / fill / scroll /
 * ask_user plus M6 semantic tools (including request_upload → M5
 * requestOpenThinkPicker / think-zip-picker); this module executes them
 * against a whitelist of Mission routes and panels. Role policy matches
 * rscircuit-agent `missionToolRoleDecision`. No copper viewer. No silent
 * commit / merge / delete. draft_issue never submits.
 *
 * https://github.com/alibaba/page-agent
 * https://developers.cloudflare.com/agents/harnesses/think/client-tools/
 */

import {
  endMissionDrive,
  getMissionDrive,
  highlightFromElement,
  requestMissionDraftIssue,
  requestMissionSelectFile,
  requestMissionSetFilter,
  setMissionHighlight,
  type MissionHighlight,
} from "./mission-drive.ts";
import { isRevisionId, revIdFromFilesPath } from "./kicad-path.ts";
import { readFilesViewerStatus, waitForFilesViewerOpen, type FilesViewerStatus } from "./files-viewer-open.ts";
import { nearestScrollRoot } from "./scroll-root.ts";
import { OPEN_THINK_EVENT, requestOpenThink, requestOpenThinkPicker } from "./underline-nav.ts";

const FILES_REV_IN_PATH = /(\/(?:files|hardware)\/revisions\/)([^/?#]+)/;

export const MISSION_PAGE_SCHEMA = "pcbx.mission_page/1" as const;
export const SELECTION_SCHEMA = "pcbx.selection/1" as const;
export const MISSION_NODE_LIMIT = 80;
/** Persisted / model-facing node cap — in-memory index stays at MISSION_NODE_LIMIT. */
export const MISSION_CONTEXT_NODE_LIMIT = 24;
export const MISSION_CONTEXT_NAME_CHARS = 40;
export const MISSION_CONTEXT_SELECTION_ITEMS = 3;
export const SCROLL_DEFAULT_PX = 480;
export const SCROLL_MAX_PX = 2000;
export const OPEN_MISSION_PANEL_EVENT = "mission:open-panel";

export const MISSION_NAV_PRIMITIVE_TOOL_NAMES = [
  "get_page_context",
  "navigate",
  "click",
  "fill",
  "scroll",
  "ask_user",
] as const;

export const MISSION_NAV_SEMANTIC_TOOL_NAMES = [
  "open_file",
  "open_change",
  "open_issue",
  "set_filter",
  "draft_issue",
  "open_job",
  "request_upload",
] as const;

export const MISSION_NAV_CLIENT_TOOL_NAMES = [
  ...MISSION_NAV_PRIMITIVE_TOOL_NAMES,
  ...MISSION_NAV_SEMANTIC_TOOL_NAMES,
] as const;

export const MISSION_NAV_READ_TOOL_NAMES = [
  "get_page_context",
  "navigate",
  "click",
  "scroll",
  "ask_user",
  "open_file",
  "open_change",
  "open_issue",
  "set_filter",
  "open_job",
  "request_upload",
  "wait",
  "done",
] as const;

export const MISSION_NAV_MEMBER_TOOL_NAMES = ["draft_issue"] as const;

export type MissionNavClientToolName = (typeof MISSION_NAV_CLIENT_TOOL_NAMES)[number];

export const MISSION_FILTER_PAGES = ["files", "issues", "changes", "jobs"] as const;

export const MISSION_FILTERS_BY_PAGE = {
  files: ["tree", "materials"] as const,
  issues: ["open", "closed", "all"] as const,
  changes: ["open", "closed", "all"] as const,
  jobs: ["queued", "running", "succeeded", "failed", "cancelled", "all"] as const,
} as const;

export const MISSION_ALLOWED_PANELS = [
  "think",
  "materials",
  "tree",
  "upload",
  "findings",
  "new-review",
  "members",
  "git",
  "lib",
  "mcp",
  "files",
  "issues",
  "changes",
  "agents",
  "settings",
  "jobs",
  "requirements",
  "milestones",
] as const;

export type MissionPanelId = (typeof MISSION_ALLOWED_PANELS)[number];

const LEGACY_REST: Record<string, string> = {
  hardware: "files",
  reviews: "changes",
  mind: "agents",
};

const SETTINGS_TABS = new Set(["members", "git", "lib", "mcp"]);

const ID_SEGMENT = /^(?!\.\.?$)[A-Za-z0-9._-]+$/;

const DANGER_TESTIDS = new Set([
  "materials-upload",
  "materials-upload-input",
  "hardware-empty-upload",
  "create-project-submit",
  "hw-zip-input",
  "files-open-think-upload",
  "think-zip-picker",
]);

const DANGER_NAME = /\b(delete|merge|commit|upload|remove|destroy)\b/i;

const ROLE_WHITELIST = new Set([
  "button",
  "link",
  "tab",
  "textbox",
  "searchbox",
  "combobox",
  "menuitem",
  "checkbox",
  "radio",
  "switch",
]);

export type SelectionItem = {
  kind: "symbol" | "footprint" | "pad" | "label" | "zone" | "other";
  refdes: string | null;
  uuid: string | null;
  value: string | null;
  lib_id: string | null;
  net: string | null;
  sheet_path: string | null;
};

export type SelectionSnapshot = {
  schema: "pcbx.selection/1";
  at: string;
  surface: "schematic" | "pcb" | "none";
  document_path: string | null;
  git_oid: string | null;
  items: SelectionItem[];
  source: "editor_event" | "overlay_click" | "deep_link" | "agent_focus" | "cleared";
};

export type MissionDomNode = {
  index: number;
  role: string;
  name: string;
  route?: string;
  panel?: string;
  testid?: string;
  mission_id?: string;
  danger?: boolean;
  fillable?: boolean;
};

export type MissionPageContext = {
  schema: "pcbx.mission_page/1";
  route: string;
  title: string;
  nodes: MissionDomNode[];
  open_panels: string[];
  selection: SelectionSnapshot;
  viewer?: FilesViewerStatus;
};

export type MissionToolResult = {
  ok: boolean;
  error?: string;
  reason?: string;
  context?: MissionPageContext;
  opened_panel?: string;
  submitted?: boolean;
  drafted?: boolean;
  opened?: boolean;
  paused?: boolean;
  highlight?: MissionHighlight;
  filter?: string;
  page?: string;
  path?: string;
  id?: string;
};

export type AskUserPending = {
  pending: true;
  tool: "ask_user";
  question: string;
  choices?: string[];
};

export type MissionLocation = {
  pathname: string;
  search: string;
  hash: string;
  title: string;
};

export type MissionActionTarget = {
  role: string;
  name: string;
  route?: string;
  panel?: string;
  testid?: string;
  missionId?: string;
  danger?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  fillable?: boolean;
  click(): void;
  fill(value: string): { ok: boolean; reason?: string };
};

export type MissionNavigatorDeps = {
  getLocation(): MissionLocation;
  getProjectBase(): string | null;
  navigate(to: string): void;
  listNodes(): MissionActionTarget[];
  listOpenPanels(): string[];
  getSelection(): SelectionSnapshot | null;
  scroll(direction: "up" | "down", amount: number): { ok: boolean; reason?: string };
  openPanel(panel: MissionPanelId): { ok: boolean; reason?: string };
  now(): string;
  getRole(): string | null;
  draftIssue(input: { title: string; body: string }): { ok: boolean; submitted: boolean; reason?: string };
  selectFile(path: string): { ok: boolean; reason?: string; highlight?: MissionHighlight };
  setFilter(page: string, filter: string): { ok: boolean; reason?: string };
  getHighlight?(missionId: string): MissionHighlight | null;
  waitForFileOpened?(
    path: string,
    opts?: { signal?: AbortSignal },
  ): Promise<{ ok: boolean; reason?: string; error?: string }>;
};

export type ProjectScope = {
  org: string;
  project: string;
  rest: string;
};

export function clearedSelection(at: string): SelectionSnapshot {
  return {
    schema: SELECTION_SCHEMA,
    at,
    surface: "none",
    document_path: null,
    git_oid: null,
    items: [],
    source: "cleared",
  };
}

export function parseProjectPath(pathname: string): ProjectScope | null {
  const match = pathname.match(/^\/o\/([^/]+)\/p\/([^/]+)(?:\/(.*))?$/);
  if (!match) return null;
  return { org: match[1]!, project: match[2]!, rest: match[3] ?? "" };
}

export function splitRest(rest: string): { path: string; search: string; hash: string } {
  const hashIdx = rest.indexOf("#");
  const withoutHash = hashIdx >= 0 ? rest.slice(0, hashIdx) : rest;
  const hash = hashIdx >= 0 ? rest.slice(hashIdx) : "";
  const qIdx = withoutHash.indexOf("?");
  const path = (qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash).replace(/^\/+|\/+$/g, "");
  const search = qIdx >= 0 ? withoutHash.slice(qIdx) : "";
  return { path, search, hash };
}

export function canonicalizeMissionRest(rest: string): string | null {
  const { path } = splitRest(rest);
  if (!path) return "files";
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 0) return "files";
  const head = LEGACY_REST[parts[0]!] ?? parts[0]!;
  const tail = parts.slice(1);
  if (head === "hardware") return canonicalizeMissionRest(["files", ...tail].join("/"));
  return [head, ...tail].join("/");
}

export function isAllowedMissionRest(rest: string): boolean {
  const canonical = canonicalizeMissionRest(rest);
  if (canonical === null) return false;
  if (canonical === "files" || canonical === "issues" || canonical === "changes" || canonical === "agents") {
    return true;
  }
  if (canonical === "settings" || canonical === "requirements" || canonical === "jobs") return true;
  if (canonical === "milestones" || canonical === "releases" || canonical === "help/mcp") return true;
  if (canonical.startsWith("settings/")) {
    const tab = canonical.slice("settings/".length);
    return SETTINGS_TABS.has(tab) && !tab.includes("/");
  }
  const prefixed: Array<[string, number]> = [
    ["files/revisions/", 1],
    ["issues/", 1],
    ["changes/", 1],
    ["requirements/", 1],
    ["jobs/", 1],
  ];
  for (const [prefix, segs] of prefixed) {
    if (!canonical.startsWith(prefix)) continue;
    const id = canonical.slice(prefix.length);
    const bits = id.split("/");
    return bits.length === segs && bits.every((bit) => ID_SEGMENT.test(bit));
  }
  return false;
}

export function isAllowedMissionPanel(panel: string): panel is MissionPanelId {
  return (MISSION_ALLOWED_PANELS as readonly string[]).includes(panel);
}

export function isAllowedMissionFilter(page: string, filter: string): boolean {
  if (!(MISSION_FILTER_PAGES as readonly string[]).includes(page)) return false;
  const allowed = MISSION_FILTERS_BY_PAGE[page as keyof typeof MISSION_FILTERS_BY_PAGE];
  return (allowed as readonly string[]).includes(filter);
}

export function isAllowedMissionId(id: string): boolean {
  return ID_SEGMENT.test(id.trim());
}

export function isAllowedMissionFilePath(path: string): boolean {
  const trimmed = path.trim();
  if (!trimmed) return false;
  if (rejectExternalRoute(trimmed)) return false;
  if (trimmed.startsWith("/") || trimmed.includes("\\")) return false;
  const parts = trimmed.split("/").filter(Boolean);
  if (parts.length < 1 || parts.length > 16) return false;
  return parts.every((part) => part !== "." && part !== ".." && !part.includes("\\") && !part.includes("\0"));
}

export function filePathFromSearch(search: string | null | undefined): string | null {
  const raw = (search ?? "").trim();
  if (!raw) return null;
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const file = params.get("file")?.trim();
  return file && isAllowedMissionFilePath(file) ? file : null;
}

export type FilesLocation = Pick<Location, "pathname" | "search" | "hash">;

/** Next Files URL after updating hosted revision and/or selected file. */
export function nextFilesLocation(
  loc: FilesLocation,
  update: { rev?: string; file?: string },
): FilesLocation | null {
  if (update.rev !== undefined && !isRevisionId(update.rev)) return null;
  if (update.file !== undefined && !isAllowedMissionFilePath(update.file)) return null;
  let pathname = loc.pathname;
  const rawSearch = (loc.search ?? "").trim();
  const params = new URLSearchParams(rawSearch.startsWith("?") ? rawSearch.slice(1) : rawSearch);
  if (update.rev !== undefined) {
    if (revIdFromFilesPath(pathname) !== null) {
      pathname = pathname.replace(FILES_REV_IN_PATH, `$1${encodeURIComponent(update.rev)}`);
    } else {
      params.set("rev", update.rev);
    }
  }
  if (update.file !== undefined) {
    params.set("file", update.file);
  }
  const search = params.toString() ? `?${params.toString()}` : "";
  return { pathname, search, hash: loc.hash ?? "" };
}

export function filePathFromMissionRoute(route?: string | null, search?: string | null): string | null {
  if (route) {
    const extra = splitRest(route);
    const fromRoute = filePathFromSearch(extra.search);
    if (fromRoute) return fromRoute;
  }
  return filePathFromSearch(search);
}

export function filePathFromMissionId(missionId?: string | null): string | null {
  if (!missionId?.startsWith("file:")) return null;
  const path = missionId.slice("file:".length).trim();
  return path && isAllowedMissionFilePath(path) ? path : null;
}

export function filePathFromFileRowHint(node: {
  missionId?: string;
  testid?: string;
  name?: string;
  route?: string;
}): string | null {
  const fromId = filePathFromMissionId(node.missionId);
  if (fromId) return fromId;
  const fromRoute = filePathFromMissionRoute(node.route);
  if (fromRoute) return fromRoute;
  const testid = node.testid?.trim() ?? "";
  if (testid.startsWith("file-row-")) {
    const path = testid.slice("file-row-".length).trim();
    if (path && isAllowedMissionFilePath(path)) return path;
  }
  const name = node.name?.trim() ?? "";
  if (name && isAllowedMissionFilePath(name) && /\.kicad_(sch|pcb|pro)$/i.test(name)) return name;
  return null;
}

const MISSION_MEMBER_ROLES = new Set(["owner", "editor", "hardware", "firmware", "review"]);

export function isMissionGuestRole(role: string | null | undefined): boolean {
  return !isMissionMemberRole(role);
}

export function isMissionMemberRole(role: string | null | undefined): boolean {
  return role != null && MISSION_MEMBER_ROLES.has(role);
}

export function isGuestNavigationTarget(target: { role?: string; route?: string }): boolean {
  if (target.route) return true;
  return target.role === "link" || target.role === "tab";
}

const IDENTITY_REQUIRED_REASON =
  "No verified project role on this call. Reconnect Think — writes are not assumed guest.";

export function missionToolRoleDecision(
  toolName: string,
  role: string | null | undefined,
  opts?: { identityMissing?: boolean },
): { action: "block"; reason: string } | undefined {
  const known =
    (MISSION_NAV_CLIENT_TOOL_NAMES as readonly string[]).includes(toolName) ||
    toolName === "wait" ||
    toolName === "done";
  if (!known) return undefined;
  const write = toolName === "fill" || (MISSION_NAV_MEMBER_TOOL_NAMES as readonly string[]).includes(toolName);
  if (opts?.identityMissing && write) {
    return { action: "block", reason: IDENTITY_REQUIRED_REASON };
  }
  if (toolName === "fill" && isMissionGuestRole(role)) {
    return { action: "block", reason: "Guests cannot fill Mission fields." };
  }
  if ((MISSION_NAV_MEMBER_TOOL_NAMES as readonly string[]).includes(toolName)) {
    if (!isMissionMemberRole(role)) {
      return {
        action: "block",
        reason:
          "draft_issue requires a project member (owner/editor/hardware/firmware/review). Guest and viewer are read-only.",
      };
    }
    return undefined;
  }
  if (isMissionGuestRole(role) && !(MISSION_NAV_READ_TOOL_NAMES as readonly string[]).includes(toolName)) {
    return {
      action: "block",
      reason: "Guests may only use read and navigation Mission tools. Click is limited to links and tabs.",
    };
  }
  return undefined;
}

export function rejectExternalRoute(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return "empty_route";
  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith("http:") ||
    lower.startsWith("https:") ||
    lower.startsWith("javascript:") ||
    lower.startsWith("data:") ||
    lower.startsWith("//")
  ) {
    return "external_url";
  }
  if (trimmed.includes("\\") || trimmed.includes("..")) return "unsafe_path";
  return null;
}

export type ResolvedMissionNav = {
  pathname: string;
  search: string;
  hash: string;
  rest: string;
};

export function resolveMissionNavigate(input: {
  route?: string;
  panel?: string;
  location: MissionLocation;
  projectBase: string;
}): { ok: true; target: ResolvedMissionNav; panel?: MissionPanelId } | { ok: false; error: string; reason: string } {
  const external = input.route ? rejectExternalRoute(input.route) : null;
  if (external) return { ok: false, error: external, reason: "Only in-project Mission routes are allowed." };

  const scope = parseProjectPath(input.location.pathname);
  const baseScope = parseProjectPath(input.projectBase);
  if (!scope || !baseScope) {
    return { ok: false, error: "not_on_mission", reason: "Navigate is limited to /o/:org/p/:project Mission pages." };
  }
  if (scope.org !== baseScope.org || scope.project !== baseScope.project) {
    return { ok: false, error: "cross_project", reason: "Cannot navigate to another org or project." };
  }

  let rest = scope.rest;
  if (input.route) {
    const raw = input.route.trim();
    if (raw.startsWith("/")) {
      const parsed = parseProjectPath(splitRest(raw).path.startsWith("o/") ? `/${splitRest(raw).path}` : raw.split(/[?#]/)[0]!);
      if (!parsed) {
        return { ok: false, error: "not_on_mission", reason: "Absolute paths must stay under the current project." };
      }
      if (parsed.org !== baseScope.org || parsed.project !== baseScope.project) {
        return { ok: false, error: "cross_project", reason: "Cannot navigate to another org or project." };
      }
      const extra = splitRest(raw.replace(/^\/o\/[^/]+\/p\/[^/]+\/?/, ""));
      rest = extra.path;
      if (!isAllowedMissionRest(rest)) {
        return { ok: false, error: "route_not_allowed", reason: `Route "${rest || "/"}" is not a Mission whitelist path.` };
      }
      return finishResolve(baseScope, rest, extra.search, extra.hash, input.panel);
    }
    const extra = splitRest(raw);
    rest = extra.path;
    if (!isAllowedMissionRest(rest)) {
      return { ok: false, error: "route_not_allowed", reason: `Route "${rest || "/"}" is not a Mission whitelist path.` };
    }
    return finishResolve(baseScope, rest, extra.search, extra.hash, input.panel);
  }

  if (!isAllowedMissionRest(rest) && rest !== "") {
    return { ok: false, error: "route_not_allowed", reason: "Current path is not a Mission whitelist route." };
  }
  return finishResolve(baseScope, rest || "files", input.location.search, input.location.hash, input.panel);
}

function finishResolve(
  base: ProjectScope,
  rest: string,
  search: string,
  hash: string,
  panel?: string,
): { ok: true; target: ResolvedMissionNav; panel?: MissionPanelId } | { ok: false; error: string; reason: string } {
  const canonical = canonicalizeMissionRest(rest);
  if (canonical === null || !isAllowedMissionRest(canonical)) {
    return { ok: false, error: "route_not_allowed", reason: `Route "${rest}" is not a Mission whitelist path.` };
  }
  let nextRest = canonical;
  let nextSearch = search;
  let nextHash = hash;
  let applied: MissionPanelId | undefined;
  if (panel) {
    if (!isAllowedMissionPanel(panel)) {
      return { ok: false, error: "panel_not_allowed", reason: `Panel "${panel}" is not a Mission whitelist panel.` };
    }
    applied = panel;
    const mapped = applyPanelToRest(canonical, panel);
    nextRest = mapped.rest;
    if (mapped.search !== undefined) nextSearch = mapped.search;
    if (mapped.hash !== undefined) nextHash = mapped.hash;
  }
  return {
    ok: true,
    panel: applied,
    target: {
      rest: nextRest,
      pathname: `/o/${base.org}/p/${base.project}/${nextRest}`,
      search: nextSearch,
      hash: nextHash,
    },
  };
}

export function applyPanelToRest(
  rest: string,
  panel: MissionPanelId,
): { rest: string; search?: string; hash?: string } {
  switch (panel) {
    case "think":
      return { rest };
    case "materials":
    case "tree":
    case "upload":
    case "files":
      return { rest: rest.startsWith("files") ? rest : "files", hash: panel === "upload" ? "" : undefined };
    case "new-review":
      return { rest: "changes", search: "?new=1" };
    case "findings":
    case "changes":
      return { rest: rest.startsWith("changes") ? rest : "changes" };
    case "issues":
      return { rest: rest.startsWith("issues") ? rest : "issues" };
    case "agents":
      return { rest: "agents" };
    case "members":
    case "git":
    case "lib":
    case "mcp":
      return { rest: `settings/${panel}` };
    case "settings":
      return { rest: rest.startsWith("settings") ? rest : "settings" };
    case "jobs":
      return { rest: rest.startsWith("jobs") ? rest : "jobs" };
    case "requirements":
      return { rest: rest.startsWith("requirements") ? rest : "requirements" };
    case "milestones":
      return { rest: "milestones" };
    default:
      return { rest };
  }
}

export function isDangerNode(node: {
  testid?: string;
  name: string;
  danger?: boolean;
  role?: string;
}): boolean {
  if (node.danger) return true;
  if (node.testid && DANGER_TESTIDS.has(node.testid)) return true;
  if (node.testid && /upload|delete|merge|commit/i.test(node.testid)) return true;
  if (DANGER_NAME.test(node.name) && node.role !== "link") return true;
  return false;
}

/** JSON-safe `{path,state}` only — models must be able to parse get_page_context.viewer. */
export function compactFilesViewer(viewer?: FilesViewerStatus | null): FilesViewerStatus {
  const path = typeof viewer?.path === "string" && viewer.path.trim() ? viewer.path : null;
  const raw = viewer?.state;
  const state: FilesViewerStatus["state"] =
    raw === "mounted" || raw === "error" || raw === "loading" || raw === "idle" ? raw : path ? "loading" : "idle";
  return { path, state: path ? (state === "idle" ? "loading" : state) : "idle" };
}

/** Files viewer / file-row nodes. Compaction keeps pane + current file, then shell, then other rows. */
export function isViewerMissionNode(node: {
  mission_id?: string;
  testid?: string;
  route?: string;
}): boolean {
  if (node.mission_id?.startsWith("file:")) return true;
  const testid = node.testid ?? "";
  if (testid.startsWith("file-row-") || testid === "files-viewer-pane") return true;
  return typeof node.route === "string" && node.route.includes("file=");
}

function filesViewerPaneNode(node: { testid?: string }): boolean {
  return node.testid === "files-viewer-pane";
}

function contextDocumentPath(ctx: MissionPageContext): string | null {
  const fromSelection = ctx.selection.document_path?.trim();
  if (fromSelection) return fromSelection;
  const fromViewer = ctx.viewer?.path?.trim();
  return fromViewer || null;
}

function isCurrentFileMissionNode(
  node: MissionDomNode,
  currentPath: string | null,
): boolean {
  if (!currentPath) return false;
  const path = filePathFromFileRowHint({
    missionId: node.mission_id,
    testid: node.testid,
    name: node.name,
    route: node.route,
  });
  return path === currentPath;
}

function compactContextNode(node: MissionDomNode): MissionDomNode {
  const next: MissionDomNode = {
    index: node.index,
    role: node.role,
    name: node.name.length > MISSION_CONTEXT_NAME_CHARS ? node.name.slice(0, MISSION_CONTEXT_NAME_CHARS) : node.name,
  };
  if (node.route) next.route = node.route;
  if (node.testid) next.testid = node.testid;
  if (node.panel) next.panel = node.panel;
  if (node.danger) next.danger = true;
  if (node.fillable) next.fillable = true;
  return next;
}

/** Compact page context for tool results / model context (not the in-memory click index). */
export function compactMissionPageContext(ctx: MissionPageContext): MissionPageContext {
  const currentPath = contextDocumentPath(ctx);
  const paneAndCurrent: MissionDomNode[] = [];
  const shell: MissionDomNode[] = [];
  const otherFileRows: MissionDomNode[] = [];
  for (const node of ctx.nodes) {
    if (filesViewerPaneNode(node) || isCurrentFileMissionNode(node, currentPath)) {
      paneAndCurrent.push(node);
    } else if (isViewerMissionNode(node)) {
      otherFileRows.push(node);
    } else {
      shell.push(node);
    }
  }
  return {
    schema: ctx.schema,
    route: ctx.route,
    title: ctx.title.length > 80 ? ctx.title.slice(0, 80) : ctx.title,
    nodes: [...paneAndCurrent, ...shell, ...otherFileRows]
      .slice(0, MISSION_CONTEXT_NODE_LIMIT)
      .map(compactContextNode),
    open_panels: ctx.open_panels.slice(0, 8),
    selection: {
      ...ctx.selection,
      items: ctx.selection.items.slice(0, MISSION_CONTEXT_SELECTION_ITEMS),
    },
    viewer: compactFilesViewer(ctx.viewer ?? readFilesViewerStatus()),
  };
}

export function dehydrateNodes(targets: MissionActionTarget[]): MissionDomNode[] {
  const nodes: MissionDomNode[] = [];
  for (const target of targets) {
    if (target.hidden || target.disabled) continue;
    if (nodes.length >= MISSION_NODE_LIMIT) break;
    const node: MissionDomNode = {
      index: nodes.length,
      role: target.role,
      name: target.name.slice(0, 120),
    };
    if (target.route) node.route = target.route;
    if (target.panel) node.panel = target.panel;
    if (target.testid) node.testid = target.testid;
    if (target.missionId) node.mission_id = target.missionId;
    if (target.fillable) node.fillable = true;
    if (isDangerNode(target)) node.danger = true;
    nodes.push(node);
  }
  return nodes;
}

export function askUserFromInput(input: unknown): AskUserPending | { ok: false; error: string; reason: string } {
  if (input === null || typeof input !== "object") {
    return { ok: false, error: "invalid_ask_user", reason: "ask_user requires a question." };
  }
  const rec = input as { question?: unknown; prompt?: unknown; choices?: unknown };
  const question =
    typeof rec.question === "string" && rec.question.trim()
      ? rec.question.trim()
      : typeof rec.prompt === "string" && rec.prompt.trim()
        ? rec.prompt.trim()
        : "";
  if (!question) {
    return { ok: false, error: "invalid_ask_user", reason: "ask_user requires a question." };
  }
  const choices = Array.isArray(rec.choices)
    ? rec.choices.filter((c): c is string => typeof c === "string" && c.trim().length > 0)
    : undefined;
  return { pending: true, tool: "ask_user", question, choices };
}

export class MissionNavigator {
  lastContext: MissionPageContext | null = null;
  lastHighlight: MissionHighlight | null = null;
  paused = false;
  private readonly lastTargets: MissionActionTarget[] = [];
  private readonly deps: MissionNavigatorDeps;
  private openAbort = new AbortController();

  constructor(deps: MissionNavigatorDeps) {
    this.deps = deps;
  }

  pause(reason = "stop"): MissionToolResult {
    this.paused = true;
    this.lastHighlight = null;
    setMissionHighlight(null);
    this.openAbort.abort();
    this.openAbort = new AbortController();
    return { ok: true, paused: true, reason };
  }

  resume(): void {
    this.paused = false;
  }

  beforeToolCall(name: string, input?: unknown): MissionToolResult | null {
    if (this.paused || getMissionDrive().paused) {
      return { ok: false, error: "paused", reason: "Think is paused. Esc take over — human is driving." };
    }
    const role = this.deps.getRole();
    const decision = missionToolRoleDecision(name, role, { identityMissing: role == null });
    if (decision) return { ok: false, error: "role_denied", reason: decision.reason };
    if (name === "click" && isMissionGuestRole(role)) {
      const rec = asRecord(input);
      if (typeof rec.index === "number" && this.lastContext) {
        const node = this.lastContext.nodes[rec.index];
        if (node && !isGuestNavigationTarget(node)) {
          return {
            ok: false,
            error: "role_denied",
            reason: "Guests may only click navigation targets (links and tabs).",
          };
        }
      }
    }
    return null;
  }

  private rememberHighlight(highlight: MissionHighlight | null): void {
    this.lastHighlight = highlight;
    setMissionHighlight(highlight);
  }

  async getPageContext(): Promise<MissionPageContext> {
    const targets = this.deps.listNodes();
    this.lastTargets.length = 0;
    this.lastTargets.push(...targets);
    const loc = this.deps.getLocation();
    const scope = parseProjectPath(loc.pathname);
    const rest = scope ? canonicalizeMissionRest(scope.rest) ?? scope.rest : loc.pathname;
    const at = this.deps.now();
    const context: MissionPageContext = {
      schema: MISSION_PAGE_SCHEMA,
      route: rest || "files",
      title: loc.title,
      nodes: dehydrateNodes(targets),
      open_panels: this.deps.listOpenPanels(),
      selection: this.deps.getSelection() ?? clearedSelection(at),
      viewer: readFilesViewerStatus(typeof document !== "undefined" ? document : null, loc),
    };
    this.lastContext = context;
    return compactMissionPageContext(context);
  }

  async navigate(input: { route?: string; panel?: string }): Promise<MissionToolResult> {
    const projectBase = this.deps.getProjectBase();
    if (!projectBase) {
      return { ok: false, error: "not_on_mission", reason: "Navigate is limited to a project Mission page." };
    }
    const resolved = resolveMissionNavigate({
      route: input.route,
      panel: input.panel,
      location: this.deps.getLocation(),
      projectBase,
    });
    if (!resolved.ok) return { ok: false, error: resolved.error, reason: resolved.reason };
    const { target, panel } = resolved;
    this.deps.navigate(`${target.pathname}${target.search}${target.hash}`);
    let opened: string | undefined;
    if (panel) {
      const result = this.deps.openPanel(panel);
      if (!result.ok) {
        return {
          ok: false,
          error: "panel_failed",
          reason: result.reason ?? `Could not open panel "${panel}".`,
          context: await this.getPageContext(),
        };
      }
      opened = panel;
    }
    const file = filePathFromMissionRoute(input.route, target.search);
    if (file) return this.finishOpenFile(file, opened ?? "tree");
    return { ok: true, opened_panel: opened, context: await this.getPageContext() };
  }

  async click(input: { index: number }): Promise<MissionToolResult> {
    const ready = this.requireIndexed(input.index);
    if (!ready.ok) return ready;
    const { target, node } = ready;
    if (isMissionGuestRole(this.deps.getRole()) && !isGuestNavigationTarget(target) && !isGuestNavigationTarget(node)) {
      return {
        ok: false,
        error: "role_denied",
        reason: "Guests may only click navigation targets (links and tabs).",
        context: this.lastContext ?? undefined,
      };
    }
    if (node.danger || isDangerNode(target)) {
      if (target.panel && isAllowedMissionPanel(target.panel)) {
        this.deps.openPanel(target.panel);
      } else if (node.panel && isAllowedMissionPanel(node.panel)) {
        this.deps.openPanel(node.panel);
      }
      return {
        ok: false,
        error: "confirmation_required",
        reason:
          "Dangerous action (upload, merge, delete, or commit) cannot be submitted by Think. Confirm in the UI yourself.",
        opened_panel: target.panel ?? node.panel,
        context: await this.getPageContext(),
      };
    }
    const fileClick =
      filePathFromFileRowHint(node) ??
      filePathFromFileRowHint(target) ??
      filePathFromMissionRoute(target.route) ??
      filePathFromMissionId(node.mission_id) ??
      filePathFromMissionId(target.missionId);
    if (fileClick) return this.openFile({ path: fileClick });
    if (target.route) {
      return this.navigate({ route: target.route, panel: target.panel });
    }
    target.click();
    const missionId = target.missionId;
    if (missionId && this.deps.getHighlight) {
      this.rememberHighlight(this.deps.getHighlight(missionId));
    }
    return { ok: true, context: await this.getPageContext(), highlight: this.lastHighlight ?? undefined };
  }

  async fill(input: { index: number; value: string }): Promise<MissionToolResult> {
    if (isMissionGuestRole(this.deps.getRole())) {
      return {
        ok: false,
        error: "role_denied",
        reason: "Guests cannot fill Mission fields.",
        context: this.lastContext ?? undefined,
      };
    }
    const ready = this.requireIndexed(input.index);
    if (!ready.ok) return ready;
    const { target, node } = ready;
    if (node.danger || isDangerNode(target)) {
      return {
        ok: false,
        error: "confirmation_required",
        reason: "Cannot fill a danger-marked control (upload/password/commit).",
        context: this.lastContext ?? undefined,
      };
    }
    if (!target.fillable && !node.fillable) {
      return {
        ok: false,
        error: "not_fillable",
        reason: `Node ${input.index} is not a text control.`,
        context: this.lastContext ?? undefined,
      };
    }
    const filled = target.fill(input.value);
    if (!filled.ok) {
      return {
        ok: false,
        error: "fill_failed",
        reason: filled.reason ?? "Fill was rejected.",
        context: await this.getPageContext(),
      };
    }
    return { ok: true, context: await this.getPageContext(), highlight: this.lastHighlight ?? undefined };
  }

  async finishOpenFile(path: string, openedPanel = "tree"): Promise<MissionToolResult> {
    const selected = this.deps.selectFile(path);
    if (!selected.ok) {
      return {
        ok: false,
        error: "file_not_found",
        reason: selected.reason ?? `No Files tree row for "${path}".`,
        context: await this.getPageContext(),
        path,
      };
    }
    const highlight =
      selected.highlight ??
      this.deps.getHighlight?.(`file:${path}`) ??
      ({
        label: `open_file · ${path}`,
        missionId: `file:${path}`,
        top: 0,
        left: 0,
        width: 0,
        height: 0,
      } satisfies MissionHighlight);
    this.rememberHighlight({ ...highlight, label: `open_file · ${path}` });
    const opened = this.deps.waitForFileOpened
      ? await this.deps.waitForFileOpened(path, { signal: this.openAbort.signal })
      : { ok: true };
    if (!opened.ok) {
      return {
        ok: false,
        error: opened.error ?? "viewer_not_opened",
        reason: opened.reason ?? `Files viewer did not open "${path}".`,
        context: await this.getPageContext(),
        path,
      };
    }
    const liveHighlight =
      this.deps.getHighlight?.(`file:${path}`) ?? this.lastHighlight ?? highlight;
    this.rememberHighlight({ ...liveHighlight, label: `open_file · ${path}` });
    return {
      ok: true,
      path,
      opened_panel: openedPanel,
      context: await this.getPageContext(),
      highlight: this.lastHighlight ?? undefined,
    };
  }

  async openFile(input: { path: string }): Promise<MissionToolResult> {
    const path = input.path.trim();
    if (!isAllowedMissionFilePath(path)) {
      return { ok: false, error: "path_not_allowed", reason: `File path "${path}" is not a Mission whitelist path.` };
    }
    const projectBase = this.deps.getProjectBase();
    if (!projectBase) {
      return { ok: false, error: "not_on_mission", reason: "Navigate is limited to a project Mission page." };
    }
    const resolved = resolveMissionNavigate({
      route: `files?file=${encodeURIComponent(path)}`,
      panel: "tree",
      location: this.deps.getLocation(),
      projectBase,
    });
    if (!resolved.ok) return { ok: false, error: resolved.error, reason: resolved.reason };
    this.deps.navigate(`${resolved.target.pathname}${resolved.target.search}${resolved.target.hash}`);
    if (resolved.panel) this.deps.openPanel(resolved.panel);
    return this.finishOpenFile(path, resolved.panel ?? "tree");
  }

  async openChange(input: { id: string }): Promise<MissionToolResult> {
    const id = input.id.trim();
    if (!isAllowedMissionId(id)) {
      return { ok: false, error: "id_not_allowed", reason: `Change id "${id}" is not a Mission whitelist id.` };
    }
    const nav = await this.navigate({ route: `changes/${id}` });
    if (!nav.ok) return nav;
    this.rememberHighlight(
      this.deps.getHighlight?.(`change:${id}`) ?? {
        label: `open_change · ${id}`,
        missionId: `change:${id}`,
        top: 0,
        left: 0,
        width: 0,
        height: 0,
      },
    );
    return { ok: true, id, context: nav.context, highlight: this.lastHighlight ?? undefined };
  }

  async openIssue(input: { id: string }): Promise<MissionToolResult> {
    const id = input.id.trim();
    if (!isAllowedMissionId(id)) {
      return { ok: false, error: "id_not_allowed", reason: `Issue id "${id}" is not a Mission whitelist id.` };
    }
    const nav = await this.navigate({ route: `issues/${id}` });
    if (!nav.ok) return nav;
    this.rememberHighlight(
      this.deps.getHighlight?.(`issue:${id}`) ?? {
        label: `open_issue · ${id}`,
        missionId: `issue:${id}`,
        top: 0,
        left: 0,
        width: 0,
        height: 0,
      },
    );
    return { ok: true, id, context: nav.context, highlight: this.lastHighlight ?? undefined };
  }

  async openJob(input: { id: string }): Promise<MissionToolResult> {
    const id = input.id.trim();
    if (!isAllowedMissionId(id)) {
      return { ok: false, error: "id_not_allowed", reason: `Job id "${id}" is not a Mission whitelist id.` };
    }
    const nav = await this.navigate({ route: `jobs/${id}` });
    if (!nav.ok) return nav;
    this.rememberHighlight(
      this.deps.getHighlight?.(`job:${id}`) ?? {
        label: `open_job · ${id}`,
        missionId: `job:${id}`,
        top: 0,
        left: 0,
        width: 0,
        height: 0,
      },
    );
    return { ok: true, id, context: nav.context, highlight: this.lastHighlight ?? undefined };
  }

  async setFilter(input: { page: string; filter: string }): Promise<MissionToolResult> {
    const page = input.page.trim();
    const filter = input.filter.trim();
    if (page === "agents") {
      return { ok: false, error: "page_not_allowed", reason: "set_filter does not target /agents (M7)." };
    }
    if (!isAllowedMissionFilter(page, filter)) {
      return {
        ok: false,
        error: "filter_not_allowed",
        reason: `Filter "${filter}" is not allowed on page "${page}".`,
      };
    }
    const applied = this.deps.setFilter(page, filter);
    if (!applied.ok) {
      return { ok: false, error: "filter_failed", reason: applied.reason ?? "Could not apply filter." };
    }
    const nav = await this.navigate({
      route: `${page}?filter=${encodeURIComponent(filter)}`,
      panel: page === "files" ? (filter === "materials" ? "materials" : "tree") : undefined,
    });
    return { ok: nav.ok, page, filter, opened_panel: nav.opened_panel, context: nav.context, error: nav.error, reason: nav.reason };
  }

  async draftIssue(input: { title: string; body: string }): Promise<MissionToolResult> {
    const title = input.title.trim();
    const body = input.body;
    if (!title) {
      return { ok: false, error: "invalid_draft", reason: "draft_issue requires a title.", submitted: false };
    }
    const nav = await this.navigate({ route: "issues", panel: "issues" });
    if (!nav.ok) return { ...nav, submitted: false };
    const drafted = this.deps.draftIssue({ title, body });
    if (!drafted.ok) {
      return {
        ok: false,
        error: "draft_failed",
        reason: drafted.reason ?? "Could not fill the New Issue form.",
        submitted: false,
        context: nav.context,
      };
    }
    if (drafted.submitted) {
      return {
        ok: false,
        error: "submit_forbidden",
        reason: "draft_issue must never submit the create form.",
        submitted: false,
        context: await this.getPageContext(),
      };
    }
    this.rememberHighlight(
      this.deps.getHighlight?.("action:new-issue") ?? {
        label: `draft_issue · ${title}`,
        missionId: "action:new-issue",
        top: 0,
        left: 0,
        width: 0,
        height: 0,
      },
    );
    return {
      ok: true,
      drafted: true,
      submitted: false,
      context: await this.getPageContext(),
      highlight: this.lastHighlight ?? undefined,
    };
  }

  async requestUpload(): Promise<MissionToolResult> {
    if (typeof window !== "undefined") {
      requestOpenThinkPicker();
    }
    const highlight =
      this.deps.getHighlight?.("think-zip-picker") ??
      ({
        label: "request_upload · think-zip-picker",
        missionId: "think-zip-picker",
        testid: "think-zip-picker",
        top: 0,
        left: 0,
        width: 0,
        height: 0,
      } satisfies MissionHighlight);
    this.rememberHighlight({ ...highlight, label: "request_upload · think-zip-picker" });
    return {
      ok: true,
      opened_panel: "think",
      context: await this.getPageContext(),
      highlight: this.lastHighlight ?? undefined,
    };
  }

  async scroll(input: { direction: "up" | "down"; amount?: number }): Promise<MissionToolResult> {
    const amount = Math.min(
      Math.max(typeof input.amount === "number" && Number.isFinite(input.amount) ? input.amount : SCROLL_DEFAULT_PX, 1),
      SCROLL_MAX_PX,
    );
    const result = this.deps.scroll(input.direction, amount);
    if (!result.ok) {
      return {
        ok: false,
        error: "scroll_failed",
        reason: result.reason ?? "No Mission scroll root.",
        context: await this.getPageContext(),
      };
    }
    return { ok: true, context: await this.getPageContext() };
  }

  async dispatch(name: string, input: unknown): Promise<MissionToolResult | AskUserPending> {
    const blocked = this.beforeToolCall(name, input);
    if (blocked) return blocked;
    if (name === "get_page_context") {
      return { ok: true, context: await this.getPageContext() };
    }
    if (name === "navigate") {
      const rec = asRecord(input);
      return this.navigate({
        route: typeof rec.route === "string" ? rec.route : undefined,
        panel: typeof rec.panel === "string" ? rec.panel : undefined,
      });
    }
    if (name === "click") {
      const rec = asRecord(input);
      if (typeof rec.index !== "number") {
        return { ok: false, error: "invalid_index", reason: "click requires a numeric index." };
      }
      return this.click({ index: rec.index });
    }
    if (name === "fill") {
      const rec = asRecord(input);
      if (typeof rec.index !== "number" || typeof rec.value !== "string") {
        return { ok: false, error: "invalid_fill", reason: "fill requires index and value." };
      }
      return this.fill({ index: rec.index, value: rec.value });
    }
    if (name === "scroll") {
      const rec = asRecord(input);
      if (rec.direction !== "up" && rec.direction !== "down") {
        return { ok: false, error: "invalid_scroll", reason: "scroll.direction must be up or down." };
      }
      return this.scroll({
        direction: rec.direction,
        amount: typeof rec.amount === "number" ? rec.amount : undefined,
      });
    }
    if (name === "ask_user") {
      return askUserFromInput(input);
    }
    if (name === "open_file") {
      const rec = asRecord(input);
      if (typeof rec.path !== "string") {
        return { ok: false, error: "invalid_path", reason: "open_file requires path." };
      }
      return this.openFile({ path: rec.path });
    }
    if (name === "open_change") {
      const rec = asRecord(input);
      if (typeof rec.id !== "string") {
        return { ok: false, error: "invalid_id", reason: "open_change requires id." };
      }
      return this.openChange({ id: rec.id });
    }
    if (name === "open_issue") {
      const rec = asRecord(input);
      if (typeof rec.id !== "string") {
        return { ok: false, error: "invalid_id", reason: "open_issue requires id." };
      }
      return this.openIssue({ id: rec.id });
    }
    if (name === "open_job") {
      const rec = asRecord(input);
      if (typeof rec.id !== "string") {
        return { ok: false, error: "invalid_id", reason: "open_job requires id." };
      }
      return this.openJob({ id: rec.id });
    }
    if (name === "set_filter") {
      const rec = asRecord(input);
      if (typeof rec.page !== "string" || typeof rec.filter !== "string") {
        return { ok: false, error: "invalid_filter", reason: "set_filter requires page and filter." };
      }
      return this.setFilter({ page: rec.page, filter: rec.filter });
    }
    if (name === "draft_issue") {
      const rec = asRecord(input);
      if (typeof rec.title !== "string" || typeof rec.body !== "string") {
        return { ok: false, error: "invalid_draft", reason: "draft_issue requires title and body.", submitted: false };
      }
      return this.draftIssue({ title: rec.title, body: rec.body });
    }
    if (name === "done") {
      endMissionDrive();
      return { ok: true };
    }
    if (name === "request_upload") {
      return this.requestUpload();
    }
    return { ok: false, error: "unknown_tool", reason: `Not a Mission client tool: ${name}` };
  }

  private requireIndexed(
    index: number,
  ):
    | { ok: true; target: MissionActionTarget; node: MissionDomNode }
    | { ok: false; error: string; reason: string; context?: MissionPageContext } {
    if (!this.lastContext || this.lastTargets.length === 0) {
      return { ok: false, error: "no_page_context", reason: "Call get_page_context before click/fill." };
    }
    if (!Number.isInteger(index) || index < 0 || index >= this.lastContext.nodes.length) {
      return {
        ok: false,
        error: "index_out_of_range",
        reason: `Index ${index} is not in the last MissionPageContext (0..${this.lastContext.nodes.length - 1}).`,
        context: this.lastContext,
      };
    }
    const node = this.lastContext.nodes[index]!;
    const target = this.lastTargets[index];
    if (!target) {
      return {
        ok: false,
        error: "stale_context",
        reason: "The last observation no longer matches the live node list. Call get_page_context.",
        context: this.lastContext,
      };
    }
    return { ok: true, target, node };
  }
}

/** useAgentChat may pass a parsed object or the raw JSON argument string. */
export function parseMissionToolInput(input: unknown): Record<string, unknown> {
  if (input !== null && typeof input === "object" && !Array.isArray(input)) {
    return input as Record<string, unknown>;
  }
  if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) return {};
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* not JSON — fall through */
    }
  }
  return {};
}

function asRecord(input: unknown): Record<string, unknown> {
  return parseMissionToolInput(input);
}

export function requestOpenMissionPanel(panel: MissionPanelId): void {
  if (typeof window === "undefined") return;
  if (panel === "think") {
    requestOpenThink();
    return;
  }
  window.dispatchEvent(new CustomEvent(OPEN_MISSION_PANEL_EVENT, { detail: { panel } }));
}

function visibleName(el: Element): string {
  const labelled = el.getAttribute("aria-label") || el.getAttribute("title") || "";
  if (labelled.trim()) return labelled.trim();
  const text = (el.textContent || "").replace(/\s+/g, " ").trim();
  if (text) return text.slice(0, 120);
  const testid = el.getAttribute("data-testid") || el.getAttribute("data-mission-id") || "";
  return testid || el.tagName.toLowerCase();
}

function elementRole(el: Element): string {
  const explicit = el.getAttribute("role");
  if (explicit) return explicit;
  const tag = el.tagName.toLowerCase();
  if (tag === "a") return "link";
  if (tag === "button") return "button";
  if (tag === "input") {
    const type = (el.getAttribute("type") || "text").toLowerCase();
    if (type === "checkbox") return "checkbox";
    if (type === "radio") return "radio";
    if (type === "search") return "searchbox";
    return "textbox";
  }
  if (tag === "textarea") return "textbox";
  if (tag === "select") return "combobox";
  return "generic";
}

function isFillableElement(el: Element): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag === "textarea") return true;
  if (tag === "select") return true;
  if (tag !== "input") return el.getAttribute("role") === "textbox" || el.getAttribute("role") === "searchbox";
  const type = (el.getAttribute("type") || "text").toLowerCase();
  return !["button", "submit", "reset", "checkbox", "radio", "file", "hidden", "image", "password"].includes(type);
}

function isHiddenElement(el: Element): boolean {
  if (el.hasAttribute("hidden")) return true;
  if (el.getAttribute("aria-hidden") === "true") return true;
  return false;
}

function routeFromHref(href: string, projectBase: string): string | undefined {
  if (!href || href.startsWith("http") || href.startsWith("//") || href.startsWith("javascript:")) return undefined;
  const path = href.split(/[?#]/)[0] ?? "";
  if (!path.startsWith(projectBase)) return undefined;
  const rest = path.slice(projectBase.length).replace(/^\/+/, "");
  return isAllowedMissionRest(rest || "files") ? rest || "files" : undefined;
}

function highlightByMissionId(missionId: string): MissionHighlight | null {
  if (typeof document === "undefined") return null;
  const candidates = missionId.startsWith("file:")
    ? [
        missionId,
        `file:${decodeURIComponentSafe(missionId.slice("file:".length))}`,
        `file:${encodeURIComponent(decodeURIComponentSafe(missionId.slice("file:".length)))}`,
      ]
    : [missionId];
  for (const id of [...new Set(candidates)]) {
    const el =
      document.querySelector(`[data-mission-id="${cssAttr(id)}"]`) ??
      document.querySelector(`[data-testid="${cssAttr(id)}"]`);
    if (el) {
      return highlightFromElement(el, missionId, { missionId, testid: el.getAttribute("data-testid") ?? undefined });
    }
  }
  return null;
}

function decodeURIComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function cssAttr(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function fillDraftIssueFields(title: string, body: string): { ok: boolean; submitted: boolean; reason?: string } {
  if (typeof document === "undefined") {
    requestMissionDraftIssue(title, body);
    return { ok: true, submitted: false };
  }
  requestMissionDraftIssue(title, body);
  const titleEl = document.getElementById("iss-title") as HTMLInputElement | null;
  const bodyEl = document.getElementById("iss-body") as HTMLTextAreaElement | null;
  if (titleEl) {
    titleEl.value = title;
    titleEl.dispatchEvent(new Event("input", { bubbles: true }));
    titleEl.dispatchEvent(new Event("change", { bubbles: true }));
  }
  if (bodyEl) {
    bodyEl.value = body;
    bodyEl.dispatchEvent(new Event("input", { bubbles: true }));
    bodyEl.dispatchEvent(new Event("change", { bubbles: true }));
  }
  return { ok: true, submitted: false };
}

export function createBrowserMissionDeps(opts: {
  getLocation: () => MissionLocation;
  getProjectBase: () => string | null;
  navigate: (to: string) => void;
  getSelection?: () => SelectionSnapshot | null;
  getRole?: () => string | null;
}): MissionNavigatorDeps {
  return {
    getLocation: opts.getLocation,
    getProjectBase: opts.getProjectBase,
    navigate: opts.navigate,
    now: () => new Date().toISOString(),
    getSelection: () => opts.getSelection?.() ?? null,
    getRole: () => opts.getRole?.() ?? null,
    draftIssue(input) {
      return fillDraftIssueFields(input.title, input.body);
    },
    selectFile(path) {
      requestMissionSelectFile(path);
      const highlight = highlightByMissionId(`file:${path}`);
      return highlight
        ? { ok: true, highlight }
        : { ok: true, highlight: { label: `open_file · ${path}`, missionId: `file:${path}`, top: 0, left: 0, width: 0, height: 0 } };
    },
    waitForFileOpened(path, opts) {
      return waitForFilesViewerOpen(path, { signal: opts?.signal });
    },
    setFilter(page, filter) {
      requestMissionSetFilter(page, filter);
      return { ok: true };
    },
    getHighlight: highlightByMissionId,
    listOpenPanels() {
      const loc = opts.getLocation();
      const panels = new Set<string>();
      const rest = canonicalizeMissionRest(parseProjectPath(loc.pathname)?.rest ?? "") ?? "";
      if (rest.startsWith("files")) panels.add("files");
      if (rest.startsWith("issues")) panels.add("issues");
      if (rest.startsWith("changes")) panels.add("changes");
      if (rest.startsWith("agents")) panels.add("agents");
      if (rest.startsWith("settings")) panels.add("settings");
      if (rest.startsWith("jobs")) panels.add("jobs");
      if (rest.startsWith("requirements")) panels.add("requirements");
      if (rest === "milestones") panels.add("milestones");
      if (rest.startsWith("settings/")) {
        const tab = rest.slice("settings/".length);
        if (SETTINGS_TABS.has(tab)) panels.add(tab);
      }
      if (loc.search.includes("new=1")) panels.add("new-review");
      if (rest.startsWith("changes/") && rest.length > "changes/".length) panels.add("findings");
      if (typeof document !== "undefined") {
        if (document.querySelector(".app-shell.think-open")) panels.add("think");
        if (document.querySelector('[data-testid="files-materials"]')) panels.add("materials");
        if (document.querySelector('[data-testid="files-sidebar-tree"]')) panels.add("tree");
      }
      try {
        if (sessionStorage.getItem("mx.think") === "1") panels.add("think");
      } catch {
        /* ignore */
      }
      return [...panels];
    },
    listNodes() {
      if (typeof document === "undefined") return [];
      const root = document.querySelector(".app-shell") ?? document.body;
      const selected = root.querySelectorAll(
        [
          "[data-mission-id]",
          "[data-testid]",
          "a[href]",
          "button",
          "[role='button']",
          "[role='tab']",
          "[role='link']",
          "[role='menuitem']",
          "input",
          "textarea",
          "select",
          "[role='textbox']",
          "[role='searchbox']",
        ].join(","),
      );
      const seen = new Set<Element>();
      const targets: MissionActionTarget[] = [];
      const projectBase = opts.getProjectBase();
      for (const el of selected) {
        if (seen.has(el)) continue;
        seen.add(el);
        if (el.closest("[data-mission-ignore]")) continue;
        if (isHiddenElement(el)) continue;
        const role = elementRole(el);
        const missionId = el.getAttribute("data-mission-id") || undefined;
        const testid = el.getAttribute("data-testid") || undefined;
        if (!missionId && !testid && !ROLE_WHITELIST.has(role)) continue;
        const fillable = isFillableElement(el);
        const href = el.getAttribute("href") || "";
        const route =
          el.getAttribute("data-mission-route") ||
          (projectBase ? routeFromHref(href, projectBase) : undefined) ||
          undefined;
        const panel = (el.getAttribute("data-mission-panel") || undefined) as MissionPanelId | undefined;
        const danger = el.hasAttribute("data-mission-danger") || el.getAttribute("type") === "file";
        targets.push({
          role,
          name: visibleName(el),
          route,
          panel: panel && isAllowedMissionPanel(panel) ? panel : undefined,
          testid,
          missionId,
          danger,
          disabled: el.hasAttribute("disabled") || el.getAttribute("aria-disabled") === "true",
          hidden: isHiddenElement(el),
          fillable,
          click() {
            if (typeof (el as HTMLElement).click === "function") (el as HTMLElement).click();
          },
          fill(value: string) {
            const type = (el.getAttribute("type") || "").toLowerCase();
            if (type === "password" || type === "file") {
              return { ok: false, reason: "Cannot fill password or file inputs." };
            }
            if (!("value" in el)) return { ok: false, reason: "Element is not fillable." };
            (el as HTMLInputElement).value = value;
            el.dispatchEvent(new Event("input", { bubbles: true }));
            el.dispatchEvent(new Event("change", { bubbles: true }));
            return { ok: true };
          },
        });
      }
      return targets;
    },
    scroll(direction, amount) {
      if (typeof document === "undefined") return { ok: false, reason: "No document." };
      const main = document.querySelector("main");
      const fallback = document.scrollingElement as HTMLElement | null;
      type Box = HTMLElement & { parentElement: Box | null };
      const start = (main as Box | null) ?? (document.body as Box | null);
      const prefer = main as Box | null;
      const root = nearestScrollRoot(
        start,
        prefer,
        (el) => {
          const style = typeof window !== "undefined" ? window.getComputedStyle(el) : { overflowY: "auto", overflow: "auto" };
          return { overflowY: style.overflowY, overflow: style.overflow };
        },
        fallback as Box | null,
      );
      if (!root) return { ok: false, reason: "No Mission scroll root." };
      const delta = direction === "down" ? amount : -amount;
      root.scrollTop += delta;
      return { ok: true };
    },
    openPanel(panel) {
      requestOpenMissionPanel(panel);
      return { ok: true };
    },
  };
}

export { OPEN_THINK_EVENT };
